import { StandardAlertInfo } from "../../../../../shared/components/alert/StandardAlertInfo"
import { ConfirmModal } from "../../../../../shared/components/dialogs/ConfirmModal"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../localization/useDateTime"

import { formatWeekDays, sortWeekDays } from "../helpers"
import type { PendingProductMove } from "../hooks/useProductGroupAvailabilityMove"
import { getProductGroupTimeInterval, getProductGroupWeekDays } from "../productGroupAvailability"

type ProductMoveAvailabilityDialogProps = Readonly<{
    pendingMove: PendingProductMove
    onConfirm: () => void
    onCancel: () => void
}>

export function ProductMoveAvailabilityDialog({
    pendingMove,
    onConfirm,
    onCancel,
}: ProductMoveAvailabilityDialogProps) {
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const { product, group, narrowedAvailability } = pendingMove

    // what the product will be sold on once the group's limits apply
    const weekDays = narrowedAvailability.weekDays.length
        ? narrowedAvailability.weekDays
        : getProductGroupWeekDays(group.groupAvailabilityDetails)
    const timeInterval =
        narrowedAvailability.timeInterval ??
        getProductGroupTimeInterval(group.groupAvailabilityDetails, dateTimeFactory)

    const days = weekDays.length
        ? formatWeekDays(l10n, sortWeekDays(weekDays))
        : l10n.getString("products-move-availability-dialog-every-day-label")
    const hours = timeInterval
        ? `${dateTimeFactory.formatTime(timeInterval.start)} – ${dateTimeFactory.formatTime(timeInterval.end)}`
        : l10n.getString("products-move-availability-dialog-all-day-label")

    return (
        <LocalizedStrict
            id="products-move-availability-dialog"
            attrs={{ title: true, message: true, buttonLabelSubmit: true, buttonLabelCancel: true }}
            vars={{ productName: product.name, groupName: group.name, days, hours }}
        >
            <ConfirmModal
                open
                title="Narrow the product's availability?"
                message=""
                buttonLabelSubmit="Move and narrow"
                buttonLabelCancel="Cancel"
                buttonColorSubmit="primary"
                handleClick={onConfirm}
                onClose={onCancel}
            >
                {narrowedAvailability.hasNoOverlap && (
                    <LocalizedStrict id="products-move-availability-dialog-no-overlap-alert" attrs={{ title: true }}>
                        <StandardAlertInfo title="" />
                    </LocalizedStrict>
                )}
            </ConfirmModal>
        </LocalizedStrict>
    )
}
